
import Vue, { PropType } from 'vue';
import ControlBar from './control-bar/ControlBar.vue';
import Notifications from './notifications/Notifications.vue';
import { PortalTarget } from 'portal-vue';

import EventBus, { Event, Unsubscriber } from '@/core/event-bus';
import CoreController from './video-player-core.controller';
import IframeCoreController from './video-player-iframe-core.controller';
import PlayerStateRepository, { PlayerState } from './player-state';
import { sourceType } from './video-source';
import Api from '@/core/api';
import { get, isFunction } from 'lodash';
import { HlsConfig } from 'hls.js';
import { HiveConfig } from '@/core/hls/plugins/hive';
import { Splitpanes, Pane } from 'splitpanes';

export default Vue.extend({
    name: 'VideoPlayerCore',

    components: {
        ControlBar,
        Notifications,
        PortalTarget,
        Splitpanes,
        Pane
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },

        eventBus: {
            type: Object as PropType<EventBus>,
            required: true
        },

        playerStateRepository: {
            type: Object as PropType<PlayerStateRepository>,
            required: true
        },

        hasControlBar: {
            type: Boolean as PropType<boolean>,
            default: true
        },

        hideControlBar: {
            type: Boolean as PropType<boolean>,
            default: true
        },

        alwaysCollapseControlBarOnIdle: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        hideMouseOnIdle: {
            type: Boolean as PropType<boolean>,
            default: true
        },

        sourceUrl: {
            type: String as PropType<string>,
            required: true
        },

        useCredentials: {
            type: Boolean as PropType<boolean>,
            default: true
        },

        autoplay: {
            type: Boolean as PropType<boolean>,
            default: true
        },

        preload: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        restrictMetadata: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        metadataTypes: {
            type: Array as PropType<string[]>,
            default: () => ['spotme_metadata']
        },

        isIframeContent: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        // Since we use preload="none", the video element won't load the first frame, and therefore cannot use it as thumbnail
        thumbnail: {
            type: String as PropType<string>,
            default: undefined
        },

        disableNativePip: {
            type: Boolean as PropType<boolean>,
            default: true
        },

        isSideBarBelow: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        loopVideo: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        contentId: {
            type: String as PropType<string>,
            default: undefined
        },

        cmcdSessionId: {
            type: String as PropType<string>,
            default: undefined
        },

        hlsConfigOverwrites: {
            type: Object as PropType<Partial<HlsConfig>>,
            default: undefined
        },

        hiveConfig: {
            type: Object as PropType<HiveConfig>,
            default: () => ({} as unknown as HiveConfig)
        },

        hasSplitPanes: {
            type: Boolean,
            required: true
        },

        defaultSplitPaneSize: {
            type: Number,
            default: 50
        }
    },

    data() {
        return {
            controller: null as unknown as CoreController | IframeCoreController,
            iframeKey: 0,
            videoElementKey: 0,
            videoElapsedSecs: 0,
            videoDurationSecs: 0,
            resetTimeout: 0,
            isClientFullScreen: false,
            isVideoElementFullScreen: false,
            unsubscribers: [] as Unsubscriber[],
            playRequested: false
        };
    },

    computed: {
        state(): PlayerState {
            return this.playerStateRepository.state;
        },

        srcType(): string {
            return sourceType(this.sourceUrl);
        },

        crossorigin(): string | undefined {
            return this.useCredentials ? 'use-credentials' : undefined;
        },

        sideBarNotificationPortalId(): string {
            return '' + this.api.getPortalId('SIDEBAR_NOTIFICATION');
        },

        controlBarAtBottom(): boolean {
            const isMobileFullScreen = this.isSideBarBelow && this.isVideoElementFullScreen;
            return !this.state.videoHeight || !isMobileFullScreen;
        },

        isPlaying(): boolean {
            return this.playerStateRepository.state.isPlaying;
        },

        startPosition(): number {
            return this.hlsConfigOverwrites?.startPosition ?? -1;
        },

        shouldAutoPlay(): boolean {
            // In case of a manifestParsingError (for example because "no EXTM3U delimiter"), we will reset player
            // automatically, but isPlaying was not set to true yet because the video never played (isPlaying changes on videoElement.onplay)
            // So for this case only playRequested will be true, and we will try to autoplay because the user is expecting it.
            return this.autoplay || this.isPlaying || this.playRequested;
        },

        splitPaneSize(): number {
            return this.hasSplitPanes ? this.defaultSplitPaneSize : 0;
        },

        isControlBarVisible(): boolean {
            return this.state.isControlBarVisible;
        },

        hideMouseCursor(): boolean {
            return !this.isControlBarVisible && this.hideMouseOnIdle;
        }
    },

    watch: {
        sourceUrl(newVal: string, oldVal: string) {
            if (newVal === oldVal) {
                return;
            }

            this.playRequested = false;

            this.iframeKey++;
            // need a new video element otherwise it returns the subtitle tracks from both the previous and current sources
            this.videoElementKey++;
            Vue.nextTick(() => {
                const newValParsed = (newVal ?? '').split('?')[0];
                const oldValParsed = (oldVal ?? '').split('?')[0];

                // seek to previously recorded position if the base urls are the same
                const shouldSeek = !!newVal && (newValParsed === oldValParsed);

                console.debug('[VideoPlayerCore] new url, resetting controller', { newVal, oldVal, shouldSeek });
                this.resetController(shouldSeek);
            });
        },

        useCredentials() {
            this.resetController();
        },

        isIframeContent() {
            this.resetController();
        },

        startPosition(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.resetController();
            }
        },

        hlsConfigOverwrites(newVal: Partial<HlsConfig>) {
            this.controller?.updateOverrides?.(newVal);
        }
    },

    mounted(): void {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => this.onStateChange(partial)
        );
        this.onStateChange(state);
        this.unsubscribers.push(
            unsubscriber,
            this.api.onClientAction(this.onClientAction.bind(this)),
            this.eventBus.on(Event.dispose, () => this.disposeController()),
            this.eventBus.on(Event.reset, this.onReset.bind(this)),
            this.eventBus.on(Event.play, () => { this.playRequested = true; }),
            this.eventBus.on(Event.pause, () => { this.playRequested = false; }),
        );

        this.createController();
    },

    beforeDestroy(): void {
        clearTimeout(this.resetTimeout);
        this.disposeController();
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }
    },

    deactivated() {
        this.disposeController();
    },

    methods: {
        async createController() {

            if (this.isIframeContent) {
                this.controller = new IframeCoreController({
                    sourceUrl: this.sourceUrl,
                    eventBus: this.eventBus,
                    playerStateRepository: this.playerStateRepository,
                    alwaysCollapseControlBarOnIdle: this.alwaysCollapseControlBarOnIdle
                });

                await this.controller.init();
            } else {
                const videoEl = this.$refs['media-video'] as HTMLVideoElement;

                this.controller = new CoreController({
                    playerElement: this.$refs['video-player'] as Element,
                    videoElement: videoEl,
                    sourceUrl: this.sourceUrl,
                    eventBus: this.eventBus,
                    playerStateRepository: this.playerStateRepository,
                    useCredentials: this.useCredentials,
                    restrictMetadata: this.restrictMetadata,
                    metadataTypes: this.metadataTypes,
                    contentId: this.contentId,
                    cmcdSessionId: this.cmcdSessionId,
                    hlsConfigOverwrites: this.hlsConfigOverwrites,
                    hiveConfig: this.hiveConfig,
                    alwaysCollapseControlBarOnIdle: this.alwaysCollapseControlBarOnIdle
                });

                if (!this.sourceUrl) {
                    console.debug('[VideoPlayerCore] no source url provided');
                    return;
                }

                // Fixing this value here because it might change during the await below
                const shouldAutoPlay = this.shouldAutoPlay;

                console.debug('[VideoPlayerCore] resolve preload and autoplay', {
                    autoplay: this.autoplay,
                    preload: this.preload,
                    isPlaying: this.isPlaying,
                    shouldAutoPlay,
                    playRequested: this.playRequested
                });

                if (shouldAutoPlay || this.preload) {
                    await this.controller.init();
                }

                if (shouldAutoPlay) {
                    this.controller.tryAutoplay();
                }
            }
        },

        onStateChange(state: Partial<PlayerState>): void {
            this.isVideoElementFullScreen = get(state, 'isVideoElementFullScreen', this.isVideoElementFullScreen);
            this.videoElapsedSecs = get(state, 'videoElapsedSecs', this.videoElapsedSecs);
            this.videoDurationSecs = get(state, 'videoDurationSecs', this.videoDurationSecs);

            if (this.videoElapsedSecs < this.startPosition) {
                // When the video ends, and user clicks play, the video restarts from the beginning, if this.startPosition
                // is not in the currently downloaded fragment, hls.js will seek. But if it is inside it, or for mp4, we
                // need to seek here.
                this.api.seek(this.startPosition);
            }
        },

        onClientAction(payload: unknown, context?: string): void {
            switch (context) {
                case 'clientFullscreen':
                    this.isClientFullScreen = payload as boolean;

                    break;

                case 'resetController':
                    this.onReset(payload);

                    break;
            }
        },

        onReset(payload: unknown): void {
            if (this.resetTimeout) {
                return;
            }
            const timeout = get(payload, 'timeout', 0);

            console.log('[VideoPlayerCore] resetting controller in ' + timeout + 'ms');

            this.resetTimeout = setTimeout(() => {
                this.resetController();
            }, timeout);
        },

        disposeController() {
            if (this.controller) {
                this.controller.dispose();
                this.controller = null as unknown as CoreController | IframeCoreController;
            }
        },

        resetController(shouldSeek = false) {
            console.log('[VideoPlayerCore] resetting controller');

            clearTimeout(this.resetTimeout);
            this.resetTimeout = 0;

            this.disposeController();
            this.createController();

            if (shouldSeek && !this.isIframeContent) {
                console.log('[VideoPlayerCore] seeking to last position before reset');

                this.api.seek(this.videoElapsedSecs);
            }
        },

        onMouseEnters(): void {
            if (!this.controller) {
                return;
            }
            this.controller.revealControlBar();
        },

        onMouseLeaves(): void {
            if (!this.controller) {
                return;
            }
            this.controller.hideControlBar();
        }
    }
});
